import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { api } from '@/api/client'
import toast from 'react-hot-toast'

export interface User {
  id: string
  email: string
  agency_id: string
  role: 'agency_owner' | 'admin' | 'member'
  permissions: string[]
  agency?: {
    id: string
    name: string
    plan: string
  }
}

export interface AuthState {
  user: User | null
  token: string | null
  isAuthenticated: boolean
  isLoading: boolean
  isInitialized: boolean
}

export interface AuthActions {
  login: (email: string, password: string) => Promise<boolean>
  register: (data: RegisterData) => Promise<boolean>
  logout: () => void
  refreshToken: () => Promise<boolean>
  updateUser: (userData: Partial<User>) => void
  initialize: () => Promise<void>
}

export interface RegisterData {
  email: string
  password: string
  confirmPassword: string
  agencyName: string
  firstName: string
  lastName: string
  acceptTerms?: boolean
}

export interface LoginResponse {
  success: boolean
  data?: {
    user: User
    token: string
    refreshToken: string
  }
  error?: {
    code: string
    message: string
  }
}

export interface RegisterResponse {
  success: boolean
  data?: {
    user: User
    token: string
    refreshToken: string
  }
  error?: {
    code: string
    message: string
  }
}

export const useAuthStore = create<AuthState & AuthActions>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      isInitialized: false,

      // Actions
      login: async (email: string, password: string): Promise<boolean> => {
        set({ isLoading: true })

        try {
          const response = await api.post<LoginResponse>('/api/v1/auth/login', {
            email,
            password,
          })

          if (response.data.success && response.data.data) {
            const { user, token: accessToken, refreshToken: newRefreshToken } = response.data.data

            // Store refresh token in localStorage
            localStorage.setItem('refreshToken', newRefreshToken)

            // Update state
            set({
              user,
              token: accessToken,
              isAuthenticated: true,
              isLoading: false,
            })

            // Set auth header for future requests
            api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`

            toast.success(`Welcome back, ${user.email}!`)
            return true
          } else {
            throw new Error(response.data.error?.message || 'Login failed')
          }
        } catch (error: any) {
          console.error('Login error:', error)

          const errorMessage = error.response?.data?.error?.message ||
                              error.message ||
                              'Login failed. Please try again.'

          toast.error(errorMessage)

          set({ isLoading: false })
          return false
        }
      },

      register: async (data: RegisterData): Promise<boolean> => {
        set({ isLoading: true })

        try {
          const response = await api.post<RegisterResponse>('/api/v1/auth/register', {
            email: data.email,
            password: data.password,
            confirm_password: data.confirmPassword,
            agency_name: data.agencyName,
            first_name: data.firstName,
            last_name: data.lastName,
          })

          if (response.data.success && response.data.data) {
            const { user, token: accessToken, refreshToken: newRefreshToken } = response.data.data

            // Store refresh token in localStorage
            localStorage.setItem('refreshToken', newRefreshToken)

            // Update state
            set({
              user,
              token: accessToken,
              isAuthenticated: true,
              isLoading: false,
            })

            // Set auth header for future requests
            api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`

            toast.success('Account created successfully! Welcome to Marketing AI Platform.')
            return true
          } else {
            throw new Error(response.data.error?.message || 'Registration failed')
          }
        } catch (error: any) {
          console.error('Registration error:', error)

          const errorMessage = error.response?.data?.error?.message ||
                              error.message ||
                              'Registration failed. Please try again.'

          toast.error(errorMessage)

          set({ isLoading: false })
          return false
        }
      },

      logout: () => {
        // Clear stored tokens
        localStorage.removeItem('refreshToken')

        // Clear auth header
        delete api.defaults.headers.common['Authorization']

        // Reset state
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
        })

        toast.success('Logged out successfully')
      },

      refreshToken: async (): Promise<boolean> => {
        try {
          const refreshToken = localStorage.getItem('refreshToken')
          if (!refreshToken) {
            throw new Error('No refresh token available')
          }

          const response = await api.post<LoginResponse>('/api/v1/auth/refresh', {
            refresh_token: refreshToken,
          })

          if (response.data.success && response.data.data) {
            const { user, token, refreshToken: newRefreshToken } = response.data.data

            // Update stored refresh token
            localStorage.setItem('refreshToken', newRefreshToken)

            // Update state
            set({
              user,
              token,
              isAuthenticated: true,
            })

            // Set auth header for future requests
            api.defaults.headers.common['Authorization'] = `Bearer ${token}`

            return true
          } else {
            throw new Error('Token refresh failed')
          }
        } catch (error: any) {
          console.error('Token refresh error:', error)

          // Clear stored tokens on refresh failure
          localStorage.removeItem('refreshToken')
          delete api.defaults.headers.common['Authorization']

          // Reset auth state
          set({
            user: null,
            token: null,
            isAuthenticated: false,
          })

          return false
        }
      },

      updateUser: (userData: Partial<User>) => {
        const { user } = get()
        if (user) {
          set({
            user: { ...user, ...userData },
          })
        }
      },

      initialize: async (): Promise<void> => {
        try {
          const { token } = get()

          if (token) {
            // Set auth header
            api.defaults.headers.common['Authorization'] = `Bearer ${token}`

            // Try to verify the token by fetching user profile
            try {
              const response = await api.get('/api/v1/auth/profile')

              if (response.data.success && response.data.data) {
                set({
                  user: response.data.data,
                  isAuthenticated: true,
                  isInitialized: true,
                })
                return
              }
            } catch (error) {
              // Token might be expired, try to refresh
              const refreshSuccess = await get().refreshToken()
              if (refreshSuccess) {
                set({ isInitialized: true })
                return
              }
            }
          }

          // No valid token found
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isInitialized: true,
          })
        } catch (error) {
          console.error('Auth initialization error:', error)
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isInitialized: true,
          })
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
)

// Note: With Clerk, auth state is managed by ClerkProvider.
// The store's initialize() can still be called manually if needed
// for backend-issued tokens, but auto-init on import is removed
// since Clerk handles the auth lifecycle.